/** Geometria ilustrativa da rede: nós e conexões originais, acompanhando o relevo. */
import * as THREE from "three";
import type { Rede, Trecho } from "./rede";

export type SegmentoTubo = { de: THREE.Vector3; para: THREE.Vector3; raio: number; trecho: Trecho; inicio: number; fim: number };
export const ALTURA_TUBO = 3;

/** Ao aproximar, volta ao raio do DN para não encobrir os equipamentos. */
export function raioNaDistancia(visual: number, dn: number, distancia: number) {
  const real = THREE.MathUtils.clamp((Number.isFinite(dn) && dn > 0 ? dn : 100) / 2000, 0.01, 1.5);
  return THREE.MathUtils.lerp(real, visual, THREE.MathUtils.smoothstep(distancia, 80, 1200));
}

export function caminhoTrecho(rede: Rede, trecho: Trecho, altura: (x: number, z: number) => number): THREE.Vector3[] {
  const a = rede.nos.get(trecho.de), b = rede.nos.get(trecho.para);
  if (!a || !b || ![a.x, a.z, b.x, b.z].every(Number.isFinite)) return [];
  const distancia = Math.hypot(b.x - a.x, b.z - a.z);
  if (distancia < 0.001) return [];
  const n = Math.min(64, Math.max(1, Math.ceil(distancia / 40)));
  return Array.from({ length: n + 1 }, (_, i) => {
    const t = i / n, x = a.x + (b.x - a.x) * t, z = a.z + (b.z - a.z) * t;
    return new THREE.Vector3(x, altura(x, z) + ALTURA_TUBO, z);
  });
}

export function segmentosTubulacao(rede: Rede, altura: (x: number, z: number) => number): SegmentoTubo[] {
  const segmentos: SegmentoTubo[] = [];
  const escala = THREE.MathUtils.clamp(rede.extensao / 2500, 1, 3);
  for (const trecho of rede.trechos) {
    const pontos = caminhoTrecho(rede, trecho, altura);
    // Diâmetro ampliado só na representação: o DN cadastrado não é alterado.
    const dn = Number.isFinite(trecho.dn) && trecho.dn > 0 ? trecho.dn : 100;
    const raio = THREE.MathUtils.clamp(dn / 1000 * 6, 0.65, 2.4) * escala;
    for (let i = 1; i < pontos.length; i++) segmentos.push({ de: pontos[i - 1], para: pontos[i], raio, trecho,
      inicio: (i - 1) / (pontos.length - 1), fim: i / (pontos.length - 1) });
  }
  return segmentos;
}

/** Superfície com cores por altitude e curvas de nível; não adiciona relevo fictício. */
export function geometriaTerreno(extensao: number, altura: (x: number, z: number) => number) {
  const lado = Math.max(10, extensao * 1.3);
  const terreno = new THREE.PlaneGeometry(lado, lado, 160, 160);
  terreno.rotateX(-Math.PI / 2);
  const pos = terreno.attributes.position;
  let min = Infinity, max = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    const y = altura(pos.getX(i), pos.getZ(i)) - 1.5;
    pos.setY(i, y); min = Math.min(min, y); max = Math.max(max, y);
  }
  const cores = new Float32Array(pos.count * 3);
  const baixo = new THREE.Color("#17272c"), alto = new THREE.Color("#394b47"), cor = new THREE.Color();
  for (let i = 0; i < pos.count; i++) {
    cor.copy(baixo).lerp(alto, (pos.getY(i) - min) / (max - min || 1));
    cor.toArray(cores, i * 3);
  }
  terreno.setAttribute("color", new THREE.BufferAttribute(cores, 3));
  terreno.computeVertexNormals();

  const curvas: number[] = [], index = terreno.index!;
  // Até oito cotas intermediárias. Em terreno plano não há isolinhas artificiais.
  if (max - min > 0.3) {
    for (let nivel = 1; nivel <= 8; nivel++) {
      const cota = min + (max - min) * nivel / 9;
      for (let i = 0; i < index.count; i += 3) {
        const ids = [index.getX(i), index.getX(i + 1), index.getX(i + 2)];
        const cruzamentos: number[][] = [];
        for (let j = 0; j < 3; j++) {
          const a = ids[j], b = ids[(j + 1) % 3], ya = pos.getY(a), yb = pos.getY(b);
          if ((ya < cota && yb >= cota) || (yb < cota && ya >= cota)) {
            const t = (cota - ya) / (yb - ya);
            cruzamentos.push([pos.getX(a) + (pos.getX(b) - pos.getX(a)) * t, cota + 0.12,
              pos.getZ(a) + (pos.getZ(b) - pos.getZ(a)) * t]);
          }
        }
        if (cruzamentos.length === 2) curvas.push(...cruzamentos[0], ...cruzamentos[1]);
      }
    }
  }
  const contornos = new THREE.BufferGeometry();
  contornos.setAttribute("position", new THREE.Float32BufferAttribute(curvas, 3));
  // Referência ortogonal discreta, sem as diagonais da triangulação do terreno.
  const grade: number[] = [];
  for (let i = 0; i <= 20; i++) for (let j = 0; j < 80; j++) {
    const fixo = -lado / 2 + i * lado / 20, a = -lado / 2 + j * lado / 80, b = a + lado / 80;
    grade.push(fixo, altura(fixo, a) - 1.25, a, fixo, altura(fixo, b) - 1.25, b);
    grade.push(a, altura(a, fixo) - 1.25, fixo, b, altura(b, fixo) - 1.25, fixo);
  }
  const referencia = new THREE.BufferGeometry();
  referencia.setAttribute("position", new THREE.Float32BufferAttribute(grade, 3));
  return { terreno, contornos, referencia };
}
